import { t } from '../../i18n';
import type {
  ShipmentByCustomerReport,
  ShipmentByPortVesselReport,
  ShipmentContainerBlReport,
  ShipmentExpenseAnalysisReport,
  ShipmentExpenseCategoriesReport,
  ShipmentFileReportRow,
  ShipmentMonthlySummaryReport,
  ShipmentOpenFilesReport,
} from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import type { ExcelCell } from '../../utils/exportExcelXml';
import { escapeHtml } from '../../utils/printDocument';
import { dateCell, moneyCell } from '../customers/exportCustomerShared';
import {
  downloadReportExcel,
  printReportPdf,
  type ReportMetaLine,
} from './exportReportShared';
import type { ExportFormat } from './exportReports';

function str(value: string): ExcelCell {
  return { type: 'string', value };
}

function statusLabel(status: string): string {
  const labels = t().reports;
  return status === 'OPEN'
    ? labels.shipmentStatusOpen
    : labels.shipmentStatusInvoiced;
}

export function exportShipmentMonthlySummary(args: {
  report: ShipmentMonthlySummaryReport;
  rows: ShipmentFileReportRow[];
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.shipmentMonthlySummary;
  const meta: ReportMetaLine[] = [
    {
      label: labels.month,
      value: `${t().common.months[args.report.month - 1]} ${args.report.year}`,
    },
  ];
  const headers = [
    labels.shipmentFileNo,
    t().common.customer,
    labels.shipmentStatus,
    t().common.date,
    labels.expenseGross,
  ];
  const dataRows: ExcelCell[][] = args.rows.map((r) => [
    str(r.fileNo),
    str(r.customer.name),
    str(statusLabel(r.status)),
    dateCell(r.effectiveDate),
    moneyCell(r.expenses.gross),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: `shipment-monthly-${args.report.year}-${args.report.month}.xls`,
      title,
      meta,
      columnCount: 5,
      columnWidths: [14, 28, 16, 12, 14],
      sections: [{ headers, isNumeric: (i) => i === 4, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric: (i) => i === 4,
        rowHtml: args.rows.map(
          (r) =>
            `<tr><td>${escapeHtml(r.fileNo)}</td><td>${escapeHtml(r.customer.name)}</td><td>${escapeHtml(statusLabel(r.status))}</td><td>${escapeHtml(formatDate(r.effectiveDate))}</td><td class="num">${escapeHtml(formatMoney(r.expenses.gross))}</td></tr>`,
        ),
      },
    ],
  });
}

export function exportShipmentExpenseCategories(args: {
  report: ShipmentExpenseCategoriesReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.shipmentExpenseCategories;
  const headers = [
    t().nav.expenseCategories,
    labels.expenseNet,
    labels.expenseVat,
    labels.expenseGross,
    labels.billable,
    labels.internal,
  ];
  const dataRows: ExcelCell[][] = args.report.data.map((r) => [
    str(r.categoryName),
    moneyCell(r.totals.net),
    moneyCell(r.totals.vat),
    moneyCell(r.totals.gross),
    moneyCell(r.billable.gross),
    moneyCell(r.internal.gross),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: 'shipment-expense-categories.xls',
      title,
      columnCount: 6,
      columnWidths: [28, 14, 14, 14, 14, 14],
      sections: [{ headers, isNumeric: (i) => i > 0, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    sections: [
      {
        headers,
        isNumeric: (i) => i > 0,
        rowHtml: args.report.data.map(
          (r) =>
            `<tr><td>${escapeHtml(r.categoryName)}</td><td class="num">${escapeHtml(formatMoney(r.totals.net))}</td><td class="num">${escapeHtml(formatMoney(r.totals.vat))}</td><td class="num">${escapeHtml(formatMoney(r.totals.gross))}</td><td class="num">${escapeHtml(formatMoney(r.billable.gross))}</td><td class="num">${escapeHtml(formatMoney(r.internal.gross))}</td></tr>`,
        ),
      },
    ],
  });
}

export function exportShipmentOpenFiles(args: {
  report: ShipmentOpenFilesReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.shipmentOpenFiles;
  const headers = [
    labels.shipmentFileNo,
    t().common.customer,
    labels.port,
    labels.daysOpen,
    labels.expenseGross,
  ];
  const dataRows: ExcelCell[][] = args.report.data.map((r) => [
    str(r.fileNo),
    str(r.customer.name),
    str(r.port ?? '—'),
    { type: 'number', value: r.daysOpen },
    moneyCell(r.expenses.gross),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: 'shipment-open-files.xls',
      title,
      columnCount: 5,
      columnWidths: [14, 28, 16, 12, 14],
      sections: [{ headers, isNumeric: (i) => i >= 3, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    sections: [
      {
        headers,
        isNumeric: (i) => i >= 3,
        rowHtml: args.report.data.map(
          (r) =>
            `<tr><td>${escapeHtml(r.fileNo)}</td><td>${escapeHtml(r.customer.name)}</td><td>${escapeHtml(r.port ?? '—')}</td><td class="num">${r.daysOpen}</td><td class="num">${escapeHtml(formatMoney(r.expenses.gross))}</td></tr>`,
        ),
      },
    ],
  });
}

export function exportShipmentByCustomer(args: {
  report: ShipmentByCustomerReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.shipmentByCustomer;
  const headers = [
    t().common.customer,
    labels.fileCount,
    labels.openCount,
    labels.invoicedCount,
    labels.expenseGross,
    labels.openBillable,
  ];
  const dataRows: ExcelCell[][] = args.report.data.map((r) => [
    str(r.customerName),
    { type: 'number', value: r.fileCount },
    { type: 'number', value: r.openCount },
    { type: 'number', value: r.invoicedCount },
    moneyCell(r.expenses.gross),
    moneyCell(r.openBillable.gross),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: 'shipment-by-customer.xls',
      title,
      columnCount: 6,
      columnWidths: [28, 12, 12, 14, 14, 16],
      sections: [{ headers, isNumeric: (i) => i > 0, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    sections: [
      {
        headers,
        isNumeric: (i) => i > 0,
        rowHtml: args.report.data.map(
          (r) =>
            `<tr><td>${escapeHtml(r.customerName)}</td><td class="num">${r.fileCount}</td><td class="num">${r.openCount}</td><td class="num">${r.invoicedCount}</td><td class="num">${escapeHtml(formatMoney(r.expenses.gross))}</td><td class="num">${escapeHtml(formatMoney(r.openBillable.gross))}</td></tr>`,
        ),
      },
    ],
  });
}

export function exportShipmentContainerBl(args: {
  report: ShipmentContainerBlReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.shipmentContainerBl;
  const meta: ReportMetaLine[] = [
    { label: t().common.search, value: args.report.q },
  ];
  const headers = [
    labels.shipmentFileNo,
    t().common.customer,
    labels.blNo,
    labels.containers,
  ];
  const dataRows: ExcelCell[][] = args.report.data.map((r) => [
    str(r.fileNo),
    str(r.customer.name),
    str(r.blNo ?? '—'),
    str(r.containers.map((c) => c.containerNo).join(', ')),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: 'shipment-container-bl.xls',
      title,
      meta,
      columnCount: 4,
      columnWidths: [14, 28, 18, 32],
      sections: [{ headers, isNumeric: () => false, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric: () => false,
        rowHtml: args.report.data.map(
          (r) =>
            `<tr><td>${escapeHtml(r.fileNo)}</td><td>${escapeHtml(r.customer.name)}</td><td>${escapeHtml(r.blNo ?? '—')}</td><td>${escapeHtml(r.containers.map((c) => c.containerNo).join(', '))}</td></tr>`,
        ),
      },
    ],
  });
}

export function exportShipmentExpenseAnalysis(args: {
  report: ShipmentExpenseAnalysisReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.shipmentExpenseAnalysis;
  const headers = [
    t().common.total,
    labels.lineCount,
    labels.expenseNet,
    labels.expenseVat,
    labels.expenseGross,
  ];
  const rows = [...args.report.byBillable, ...args.report.byType];
  const dataRows: ExcelCell[][] = rows.map((r) => [
    str(r.label),
    { type: 'number', value: r.lineCount },
    moneyCell(r.totals.net),
    moneyCell(r.totals.vat),
    moneyCell(r.totals.gross),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: 'shipment-expense-analysis.xls',
      title,
      columnCount: 5,
      columnWidths: [20, 12, 14, 14, 14],
      sections: [{ headers, isNumeric: (i) => i > 0, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    sections: [
      {
        headers,
        isNumeric: (i) => i > 0,
        rowHtml: rows.map(
          (r) =>
            `<tr><td>${escapeHtml(r.label)}</td><td class="num">${r.lineCount}</td><td class="num">${escapeHtml(formatMoney(r.totals.net))}</td><td class="num">${escapeHtml(formatMoney(r.totals.vat))}</td><td class="num">${escapeHtml(formatMoney(r.totals.gross))}</td></tr>`,
        ),
      },
    ],
  });
}

export function exportShipmentByPortVessel(args: {
  report: ShipmentByPortVesselReport;
  format: ExportFormat;
}) {
  const labels = t().reports;
  const title = t().nav.shipmentByPortVessel;
  const groupLabel =
    args.report.groupBy === 'vessel'
      ? labels.groupByVessel
      : labels.groupByPort;
  const meta: ReportMetaLine[] = [{ label: labels.groupBy, value: groupLabel }];
  const headers = [groupLabel, labels.fileCount, labels.expenseGross];
  const dataRows: ExcelCell[][] = args.report.data.map((r) => [
    str(r.label),
    { type: 'number', value: r.fileCount },
    moneyCell(r.expenses.gross),
  ]);
  if (args.format === 'excel') {
    downloadReportExcel({
      filename: `shipment-by-${args.report.groupBy}.xls`,
      title,
      meta,
      columnCount: 3,
      columnWidths: [28, 12, 14],
      sections: [{ headers, isNumeric: (i) => i > 0, dataRows }],
    });
    return;
  }
  printReportPdf({
    title,
    meta,
    sections: [
      {
        headers,
        isNumeric: (i) => i > 0,
        rowHtml: args.report.data.map(
          (r) =>
            `<tr><td>${escapeHtml(r.label)}</td><td class="num">${r.fileCount}</td><td class="num">${escapeHtml(formatMoney(r.expenses.gross))}</td></tr>`,
        ),
      },
    ],
  });
}
